import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Loader2, MapPin } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Welcome back!");
      const staffRoles = ["super_admin", "admin", "manager", "user"];
      const next = location.state?.from || (staffRoles.includes(user.role) ? "/admin" : user.role === "vendor" ? "/vendor" : "/");
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-[#E5F0F0] to-[#F4F7F7]" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-8">
          <div className="relative w-12 h-12 rounded-xl overflow-hidden shadow-md" style={{background: "linear-gradient(135deg, #00B7EB 0%, #008FA4 55%, #006B85 100%)"}}>
            <div className="absolute inset-0 flex items-center justify-center">
              <MapPin className="w-6 h-6 text-white" strokeWidth={2.4} />
            </div>
          </div>
          <div className="font-display text-2xl font-bold tracking-tight" style={{color: "var(--brand-primary, #054F63)"}}>{settings.site_name || "HyderabadTourism.in"}</div>
        </Link>

        <div className="bg-white rounded-2xl border border-border p-8 shadow-sm">
          <h1 className="font-display text-3xl mb-2">Welcome back</h1>
          <p className="text-muted-foreground text-sm mb-6">Sign in to manage your bookings</p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-saffron hover:bg-saffron-dark text-white py-6 text-base" data-testid="login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            New traveller?{" "}
            <Link to="/register" className="text-teal font-semibold hover:underline" data-testid="login-register-link">Create account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
